"use client";

import { cn } from "../lib/utils";
import { useCartAssistant } from "../lib/cartAssistant/CartAssistantProvider";
import * as React from "react";

/**
 * Scrollable Message Container component
 *
 * This component provides a scrollable area for chat messages.
 * It handles automatic scrolling to the bottom when new messages arrive.
 */

export interface ScrollableMessageContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether to automatically scroll to the bottom */
  autoScroll?: boolean;
}

export function ScrollableMessageContainer({
  className,
  children,
  autoScroll = true,
  ...props
}: ScrollableMessageContainerProps) {
  const { thread, isStreaming, isWaiting } = useCartAssistant();
  const messages = thread?.thread?.messages ?? [];
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isAtBottomRef = React.useRef(true);

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = "smooth") => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current;
      scrollContainerRef.current.scrollTo({
        top: scrollHeight - clientHeight,
        behavior,
      });
    }
  }, []);

  // Handle scroll events to track if user is at bottom
  const handleScroll = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      // Use 50px buffer
      const atBottom = scrollHeight - clientHeight - scrollTop < 50;
      isAtBottomRef.current = atBottom;
    }
  }, []);

  // Auto-scroll on new messages if at bottom or if it's a new run
  React.useEffect(() => {
    if (autoScroll && messages.length > 0 && (isAtBottomRef.current || isStreaming || isWaiting)) {
      scrollToBottom();
    }
  }, [messages.length, isStreaming, isWaiting, autoScroll, scrollToBottom]);

  // Handle dynamic content changes (images loading, cards expanding)
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !autoScroll) return;

    const observer = new MutationObserver(() => {
      if (isAtBottomRef.current || isStreaming || isWaiting) {
        scrollToBottom("auto"); // Quick jump for internal content changes
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => observer.disconnect();
  }, [autoScroll, scrollToBottom, isStreaming, isWaiting]);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className={cn("flex-1 overflow-y-auto scroll-smooth", className)}
      {...props}
    >
      {children}
    </div>
  );
}
